import {
  Box,
  Center,
  getShadowByPath,
  Heading,
  HStack,
  Text,
  ThemeShadowsPath,
  VStack,
} from 'platform/foundation';
import styled, {useTheme} from 'styled-components';

import {ReactNode} from 'react';

import {isNil} from 'ramda';

import i18n from '@dms/i18n';

import {EMPTY_PLACEHOLDER, Nullish, RequiredTestIdProps, suffixTestId} from 'shared/web';

import {
  SalesLeaguePodiumMedal,
  SALES_LEAGUE_PODIUM_CARD_PADDING_HORIZONTAL,
  SALES_LEAGUE_PODIUM_IDENTITY_SPACING,
  SALES_LEAGUE_PODIUM_NAME_SPACING,
  SALES_LEAGUE_PODIUM_PLACE_BADGE_COLOR,
  SALES_LEAGUE_PODIUM_PLACE_BADGE_SIZE,
  SALES_LEAGUE_PODIUM_RUNNER_UP_PADDING_VERTICAL,
  SALES_LEAGUE_PODIUM_RUNNER_UP_VISUAL_SIZE,
  SALES_LEAGUE_PODIUM_SECTION_SPACING,
  SALES_LEAGUE_PODIUM_STAT_SPACING,
  SALES_LEAGUE_PODIUM_UNFILLED_BORDER_COLOR,
  SALES_LEAGUE_PODIUM_UNFILLED_PLACE_BADGE_COLOR,
  SALES_LEAGUE_PODIUM_UNFILLED_TEXT_COLOR,
  SALES_LEAGUE_PODIUM_WINNER_PADDING_VERTICAL,
  SALES_LEAGUE_PODIUM_WINNER_PLACE_INDEX,
  SALES_LEAGUE_PODIUM_WINNER_VISUAL_SIZE,
} from '../constants/SALES_LEAGUE_PODIUM';

/** Lifts the cards off the page without competing with the modal elevations. */
const PODIUM_SHADOW: ThemeShadowsPath = 'elevation_2';

interface PodiumSurfaceProps {
  $background: string;
  $border: string;
}

/*
  The medal gradients are the one thing `Box` cannot express — it takes a single theme
  colour — and the palette has no metallic ramps to reference, so the values come straight
  from design. Everything else about the card stays on foundation primitives.
*/
const PodiumSurface = styled.div<PodiumSurfaceProps>`
  background: ${({$background}) => $background};
  border: ${({$border}) => $border};
  border-radius: ${({theme}) => theme.radii.medium};
  box-shadow: ${({theme}) => getShadowByPath(PODIUM_SHADOW, theme)};
`;

const NO_BORDER = 'none';
const UNFILLED_BORDER_WIDTH = '1px solid';

/** The winner outranks the others in both its name and its figures. */
const WINNER_NAME_HEADING = 3;
const RUNNER_UP_NAME_HEADING = 4;
const WINNER_STAT_VALUE_HEADING = 3;
const RUNNER_UP_STAT_VALUE_HEADING = 4;

const STAT_DIVIDER_BORDER = '1px solid';
const FIRST_STAT_INDEX = 0;
/**
 * The vehicle count needs only its own width; the money columns split what is left evenly
 * so the row reaches the card edge and the rules land where the design puts them.
 */
const MONEY_STAT_FLEX = 1;
const FIRST_PLACE = 1;

export interface SalesLeaguePodiumStat {
  label: string;
  /** Already formatted, or the placeholder — the card never formats a value itself. */
  value: string;
}

export interface SalesLeaguePodiumEntry {
  /** Rep avatar on the leaderboard, branch badge on the branch board. */
  leadingVisual: ReactNode;
  title: string;
  subtitle: string;
  /** Margin stats are absent when margin is hidden for this entity, never dashed. */
  stats: SalesLeaguePodiumStat[];
}

interface SalesLeaguePodiumCardProps extends RequiredTestIdProps {
  /** Zero-based place, so 0 is gold. */
  placeIndex: number;
  medal: SalesLeaguePodiumMedal;
  /** Nullish when nobody reached this place, which renders the unfilled card. */
  entry: SalesLeaguePodiumEntry | Nullish;
  periodLabel: string;
  unfilledSubtitle: string;
  /** Stat labels the unfilled card dashes out, so the three cards line up. */
  unfilledStatLabels: string[];
}

/**
 * A slot is unfilled only when the board returned no row for it. A branch that sold
 * nothing still holds its place and shows the zeros the backend sent — it is ranked, just
 * empty-handed.
 */
export function SalesLeaguePodiumCard(props: SalesLeaguePodiumCardProps) {
  const theme = useTheme();

  const isWinner = props.placeIndex === SALES_LEAGUE_PODIUM_WINNER_PLACE_INDEX;
  const isUnfilled = isNil(props.entry);

  const visualSize = isWinner
    ? SALES_LEAGUE_PODIUM_WINNER_VISUAL_SIZE
    : SALES_LEAGUE_PODIUM_RUNNER_UP_VISUAL_SIZE;

  const textColor = isUnfilled ? SALES_LEAGUE_PODIUM_UNFILLED_TEXT_COLOR : props.medal.textColor;
  const dividerColor = isUnfilled
    ? SALES_LEAGUE_PODIUM_UNFILLED_BORDER_COLOR
    : props.medal.dividerColor;

  const stats =
    props.entry?.stats ??
    props.unfilledStatLabels.map((label) => ({label, value: EMPTY_PLACEHOLDER}));

  // The ordinal marker is locale-owned — "1." in Czech, "1e" in Dutch — so it stays a key
  // rather than a dot appended in code.
  const placeLabel = i18n.t('page.salesLeague.podium.place', {
    place: props.placeIndex + FIRST_PLACE,
  });
  const medalLabel = i18n.t(props.medal.labelKey);

  const background = isUnfilled ? theme.colors.palettes.neutral[10][100] : props.medal.background;
  const border = isUnfilled
    ? `${UNFILLED_BORDER_WIDTH} ${theme.colors.palettes.neutral[40][100]}`
    : NO_BORDER;

  return (
    <PodiumSurface
      $background={background}
      $border={border}
      data-testid={suffixTestId('card', props)}
    >
      <Box
        paddingHorizontal={SALES_LEAGUE_PODIUM_CARD_PADDING_HORIZONTAL}
        paddingVertical={
          isWinner
            ? SALES_LEAGUE_PODIUM_WINNER_PADDING_VERTICAL
            : SALES_LEAGUE_PODIUM_RUNNER_UP_PADDING_VERTICAL
        }
      >
        <VStack spacing={SALES_LEAGUE_PODIUM_SECTION_SPACING}>
          <HStack justify="space-between" align="center" spacing={2}>
            <HStack spacing={2} align="center">
              <Box
                width={SALES_LEAGUE_PODIUM_PLACE_BADGE_SIZE}
                height={SALES_LEAGUE_PODIUM_PLACE_BADGE_SIZE}
                borderRadius="small"
                backgroundColor={
                  isUnfilled
                    ? SALES_LEAGUE_PODIUM_UNFILLED_PLACE_BADGE_COLOR
                    : SALES_LEAGUE_PODIUM_PLACE_BADGE_COLOR
                }
                flexShrink={0}
                data-testid={suffixTestId('place', props)}
              >
                <Center width="100%" height="100%">
                  <Text size="xSmall" color={textColor} alternative noWrap>
                    {placeLabel}
                  </Text>
                </Center>
              </Box>
              <Text size="xSmall" color={textColor} alternative noWrap>
                {medalLabel.toUpperCase()}
              </Text>
            </HStack>
            <Text size="xSmall" color={textColor} noWrap>
              {props.periodLabel}
            </Text>
          </HStack>

          <HStack spacing={SALES_LEAGUE_PODIUM_IDENTITY_SPACING} align="center">
            {isUnfilled ? (
              <Box
                width={visualSize}
                height={visualSize}
                borderRadius="circular"
                backgroundColor={SALES_LEAGUE_PODIUM_UNFILLED_BORDER_COLOR}
              />
            ) : (
              props.entry?.leadingVisual
            )}
            <VStack spacing={SALES_LEAGUE_PODIUM_NAME_SPACING}>
              <Heading
                size={isWinner ? WINNER_NAME_HEADING : RUNNER_UP_NAME_HEADING}
                color={textColor}
              >
                {props.entry?.title ?? i18n.t('page.salesLeague.podium.unfilled')}
              </Heading>
              <Text size="small" color={textColor}>
                {props.entry?.subtitle ?? props.unfilledSubtitle}
              </Text>
            </VStack>
          </HStack>

          <Box
            borderTop={STAT_DIVIDER_BORDER}
            borderColor={dividerColor}
            paddingTop={SALES_LEAGUE_PODIUM_SECTION_SPACING}
          >
            {/* Packed to the left and ruled off from one another, so the three cards read
                as one comparison row rather than three separate blocks. */}
            <HStack spacing={SALES_LEAGUE_PODIUM_STAT_SPACING}>
              {stats.map((stat, index) => (
                <Box
                  key={stat.label}
                  flex={index === FIRST_STAT_INDEX ? undefined : MONEY_STAT_FLEX}
                  paddingLeft={index === FIRST_STAT_INDEX ? 0 : SALES_LEAGUE_PODIUM_STAT_SPACING}
                  borderLeft={index === FIRST_STAT_INDEX ? undefined : STAT_DIVIDER_BORDER}
                  borderColor={dividerColor}
                >
                  <VStack spacing={1}>
                    <Text size="xSmall" color={textColor} noWrap>
                      {stat.label}
                    </Text>
                    <Heading
                      size={isWinner ? WINNER_STAT_VALUE_HEADING : RUNNER_UP_STAT_VALUE_HEADING}
                      color={textColor}
                    >
                      {stat.value}
                    </Heading>
                  </VStack>
                </Box>
              ))}
            </HStack>
          </Box>
        </VStack>
      </Box>
    </PodiumSurface>
  );
}
